/** Compute positions of labware stacked upon other labware, modules, deck slots, etc. */

import {
  getAddressableAreaFromSlotId,
  getPositionFromSlotId,
} from '../fixtures'
import { IDENTITY_AFFINE_TRANSFORM, multiplyMatrices } from './matrixMath'
import { pairsFromArray } from './pairsFromArray'
import {
  coordinateTupleToVector3D,
  getVectorInverse,
  getVectorSum,
  IDENTITY_VECTOR,
} from './vectorMath'

import type {
  AddressableArea,
  DeckDefinition,
  LabwareDefinition,
  LabwareDefinition2,
  ModuleDefinition,
  Vector3D,
} from '../types'

/** Return dimensions of the total labware bounding box in the style of labware schema 2. */
export function getSchema2Dimensions(
  definition: LabwareDefinition
): LabwareDefinition2['dimensions'] {
  if (definition.schemaVersion === 2) {
    return definition.dimensions
  } else {
    const { backLeftBottom, frontRightTop } = definition.extents.total
    const xDimension = frontRightTop.x - backLeftBottom.x
    const yDimension = backLeftBottom.y - frontRightTop.y
    const zDimension = frontRightTop.z - backLeftBottom.z
    return { xDimension, yDimension, zDimension }
  }
}

/**
 * Return a bounding box, in coordinates relative to the labware's origin,
 * that encloses the labware when viewed from the top down.
 */
export function getLabwareViewBox(definition: LabwareDefinition): {
  /** The minimum x-coord, i.e. the left of the labware. */
  minX: number
  /** The minimum y-coord, i.e. the front of the labware. */
  minY: number
  /** The maximum x-coord, i.e. the right of the labware. */
  maxX: number
  /** The maximum y-coord, i.e. the back of the labware. */
  maxY: number
  /** The distance between left and right. */
  xDimension: number
  /** The distance between front and back. */
  yDimension: number
} {
  if (definition.schemaVersion === 2) {
    const { xDimension, yDimension } = definition.dimensions
    // In labware schema 2, the front-left is always at (0, 0) by definition.
    const minX = 0
    const minY = 0
    return {
      minX,
      minY,
      maxX: minX + xDimension,
      maxY: minY + yDimension,
      xDimension,
      yDimension,
    }
  } else {
    const { backLeftBottom, frontRightTop } = definition.extents.total
    const minX = backLeftBottom.x
    const maxX = frontRightTop.x
    const minY = frontRightTop.y
    const maxY = backLeftBottom.y
    return {
      minX,
      minY,
      maxX,
      maxY,
      xDimension: maxX - minX,
      yDimension: maxY - minY,
    }
  }
}

/** Logically specifies where a labware is located. */
export interface ComputeLabwareOriginInput {
  /** The labware and all the labware underneath it. */
  labwareDefinitionsTopToBottom: LabwareDefinition[]

  /**
   * The definition of the module underneath the labware, if there is one.
   * This should be specified even if it's only transitively underneath the labware,
   * like slot -> module -> adapter -> labware in question.
   */
  moduleDefinition: ModuleDefinition | null

  /**
   * The underlying slot. If this is a Flex and the labware is on a module, this should
   * be the slot that the module replaces.
   */
  slotId: string

  /** The underlying deck. */
  deckDefinition: DeckDefinition
}

/**
 * Computes the absolute deck coordinates of a labware.
 * Returns null if there was some internal error.
 */
export function computeLabwareOrigin(
  input: ComputeLabwareOriginInput
): Vector3D | null {
  // todo(mm, 2025-12-03): Instead of attempting physical accuracy, simply center the
  // labware in the underlying slot. https://opentrons.atlassian.net/browse/EXEC-2093
  const stack = getLabwareStackAsArray(input)

  if (stack == null) {
    console.warn(
      "Can't compute labware position. This is probably a bug in the call site, like passing in a nonexistent slot ID.",
      input
    )
    return null
  }

  const absolutePosition = getVectorSum(
    IDENTITY_VECTOR,
    ...stack.map(e => e.offset)
  )
  return absolutePosition
}

/**
 * Translate the args of `computeLabwarePosition()`, which should be optimized for
 * safety and ergonomics, into something that's easier for us to sum up.
 */
function getLabwareStackAsArray(
  input: ComputeLabwareOriginInput
): LabwareStackElement[] | null {
  const {
    deckDefinition,
    slotId,
    moduleDefinition,
    labwareDefinitionsTopToBottom,
  } = input

  if (labwareDefinitionsTopToBottom.length === 0) {
    return []
  }
  const topLabware = labwareDefinitionsTopToBottom[0]
  const bottomLabware =
    labwareDefinitionsTopToBottom[labwareDefinitionsTopToBottom.length - 1]

  const result: LabwareStackElement[] = []

  for (const [
    childLabwareDefinition,
    parentLabwareDefinition,
  ] of pairsFromArray(labwareDefinitionsTopToBottom)) {
    const parentOriginToChildOrigin = getLabwareOriginToLabwareOrigin(
      parentLabwareDefinition,
      childLabwareDefinition
    )

    // Preserving legacy misbehavior:
    // If we're computing the position of a schema 2 labware, the labware below
    // it don't contribute anything to its x or y offset. Only z.
    if (topLabware.schemaVersion === 2) {
      parentOriginToChildOrigin.x = 0
      parentOriginToChildOrigin.y = 0
    }

    result.push({
      debugInfo: {
        type: 'labwareOnLabware',
        parentLabwareDefinition,
        childLabwareDefinition,
      },
      offset: parentOriginToChildOrigin,
    })
  }

  if (moduleDefinition == null) {
    // The bottom of the stack is a labware in a deck slot.
    const slotAddressableArea = getAddressableAreaFromSlotId(
      slotId,
      deckDefinition
    )
    const slotPositionTuple = getPositionFromSlotId(slotId, deckDefinition)
    if (slotAddressableArea == null || slotPositionTuple == null) {
      return null
    }
    const slotPosition = coordinateTupleToVector3D(slotPositionTuple)
    const slotPositionToLabwareOrigin = getDeckSlotOriginToLabwareOrigin(
      slotAddressableArea,
      bottomLabware
    )
    // Preserving legacy misbehavior:
    // If we're computing the position of a schema 2 labware, the labware below
    // it don't contribute anything to its x or y offset. Only z.
    if (bottomLabware !== topLabware && topLabware.schemaVersion === 2) {
      slotPositionToLabwareOrigin.x = 0
      slotPositionToLabwareOrigin.y = 0
    }

    result.push({
      debugInfo: {
        type: 'labwareOnDeckSlot',
        parentSlotId: slotId,
        childLabwareDefinition: bottomLabware,
      },
      offset: slotPositionToLabwareOrigin,
    })
    result.push({
      debugInfo: {
        type: 'rootDeckSlot',
        slotId,
      },
      offset: slotPosition,
    })
  } else {
    // The bottom of the stack is a labware in a module in a deck slot.
    const deckSlotPositionTuple = getPositionFromSlotId(slotId, deckDefinition)
    if (deckSlotPositionTuple == null) {
      return null
    }
    const deckSlotPosition = coordinateTupleToVector3D(deckSlotPositionTuple)
    const deckSlotPositionToLabwareOrigin =
      getModuleParentOriginToLabwareOrigin(
        deckDefinition.otId,
        slotId,
        moduleDefinition,
        bottomLabware
      )

    result.push({
      debugInfo: {
        type: 'labwareOnModule',
        parentModuleDefinition: moduleDefinition,
        childLabwareDefinition: bottomLabware,
      },
      offset: deckSlotPositionToLabwareOrigin,
    })
    result.push({
      debugInfo: {
        type: 'rootModule',
        parentSlotId: slotId,
        moduleDefinition,
      },
      // Modules don't really have an origin of their own that's separate from the
      // origin of their parent deck slot.
      offset: deckSlotPosition,
    })
  }

  return result
}

interface LabwareStackElement {
  /** Offset from parent (below) to child (above). */
  offset: Vector3D

  /**
   * Human-readable metadata for the benefit of throwing in a console.log() or whatever.
   * Code should not consume this.
   */
  debugInfo:
    | {
        type: 'rootDeckSlot'
        slotId: string
      }
    | {
        type: 'rootModule'
        parentSlotId: string
        moduleDefinition: ModuleDefinition
      }
    | {
        type: 'labwareOnDeckSlot'
        parentSlotId: string
        childLabwareDefinition: LabwareDefinition
      }
    | {
        type: 'labwareOnModule'
        parentModuleDefinition: ModuleDefinition
        childLabwareDefinition: LabwareDefinition
      }
    | {
        type: 'labwareOnLabware'
        parentLabwareDefinition: LabwareDefinition
        childLabwareDefinition: LabwareDefinition
      }
}

/**
 * Return the offset from a deck slot's origin (the slot's front-left, -x, -y)
 * to a labware's origin (varies depending on the labware),
 * supposing the labware is placed in that slot.
 */
export function getDeckSlotOriginToLabwareOrigin(
  // slotDefinition is currently unused by this limited implementation. A proper
  // implementation would need it, so we're requiring it now to get callers in the habit.
  slotDefinition: AddressableArea,
  labwareDefinition: LabwareDefinition
): Vector3D {
  const slotOriginToSlotFrontLeft = IDENTITY_VECTOR

  if (labwareDefinition.schemaVersion === 2) {
    // For schema 2 labware, the labware origin is always its front-left-bottom (-x, -y, -z),
    // and it's positioned with that corner in reference to the front-left (-x, -y) of the
    // slot, plus an adjustment supplied by the labware (cornerOffsetFromSlot).
    const slotFrontLeftToLabwareFrontLeftBottom =
      labwareDefinition.cornerOffsetFromSlot
    const labwareFrontLeftBottomToLabwareOrigin = IDENTITY_VECTOR
    return getVectorSum(
      slotOriginToSlotFrontLeft,
      slotFrontLeftToLabwareFrontLeftBottom,
      labwareFrontLeftBottomToLabwareOrigin
    )
  } else {
    // For schema 3 labware, the labware origin can be at an arbitrary point in the labware.
    // Also, how the labware is positioned relative to its parent varies depending on the context.
    // Here, we're accounting for the variance in labware origin, but simplifying the positioning,
    // for now, to always put the front-left-bottom (-x, -y, -z) of the labware at the front-left of
    // the slot. This is good enough for current display purposes and matches the schema 2 behavior.
    const slotFrontLeftToLabwareFrontLeftBottom = IDENTITY_VECTOR
    const labwareOriginToLabwareFrontLeftBottom = {
      x: labwareDefinition.features.slotFootprintAsChild?.backLeft.x ?? 0,
      y: labwareDefinition.features.slotFootprintAsChild?.frontRight.y ?? 0,
      z: labwareDefinition.features.slotFootprintAsChild?.z ?? 0,
    }
    const labwareFrontLeftBottomToLabwareOrigin = getVectorInverse(
      labwareOriginToLabwareFrontLeftBottom
    )
    return getVectorSum(
      slotOriginToSlotFrontLeft,
      slotFrontLeftToLabwareFrontLeftBottom,
      labwareFrontLeftBottomToLabwareOrigin
    )
  }
}

/**
 * Given a stackup like:
 *
 * deck slot --> module --> labware
 *
 * This computes the offset from the deck slot's origin (its front-left / -x,-y corner)
 * to the labware's origin (varies depending on the labware).
 *
 * This is currently how it works even for Flex modules, even though they are not really
 * "in a slot." The slot in this case is the slot that the module replaces.
 *
 * The API is like this for transitional reasons. Ideally, we would have one function
 * to go slot->module and a second function to go module->labware.
 */
export function getModuleParentOriginToLabwareOrigin(
  deckId: string | null,
  slotId: string | null,
  moduleDefinition: ModuleDefinition,
  labwareDefinition: LabwareDefinition
): Vector3D {
  switch (labwareDefinition.schemaVersion) {
    case 2: {
      const offsetDefinedByModule = getModuleParentOriginToChildSlotOrigin(
        deckId,
        slotId,
        moduleDefinition
      )
      const offsetDefinedByLabware =
        labwareDefinition.stackingOffsetWithModule?.[moduleDefinition.model] ??
        IDENTITY_VECTOR
      return getVectorSum(offsetDefinedByModule, offsetDefinedByLabware)
    }
    case 3: {
      // todo(mm, 2025-07-02): Reconcile with the backend and compute the offset
      // based on locatingFeatures. As a first-pass approximation, this currently just
      // puts the front-left-bottom of the labware at the front-left of the module's
      // child slot, which is the traditional behavior with labware schema 2.
      const moduleParentOriginToLabwareFrontLeftBottom =
        getModuleParentOriginToChildSlotOrigin(deckId, slotId, moduleDefinition)
      const labwareOriginToLabwareFrontLeftBottom = {
        x: labwareDefinition.features.slotFootprintAsChild?.backLeft.x ?? 0,
        y: labwareDefinition.features.slotFootprintAsChild?.frontRight.y ?? 0,
        z: labwareDefinition.features.slotFootprintAsChild?.z ?? 0,
      }
      const labwareFrontLeftBottomToLabwareOrigin = getVectorInverse(
        labwareOriginToLabwareFrontLeftBottom
      )
      const moduleParentOriginToLabwareOrigin = getVectorSum(
        moduleParentOriginToLabwareFrontLeftBottom,
        labwareFrontLeftBottomToLabwareOrigin
      )
      return moduleParentOriginToLabwareOrigin
    }
  }
}

/**
 * Given a labware -> labware stackup, compute the offset from the parent (bottom)
 * labware's origin to the child (top) labware's origin.
 */
export function getLabwareOriginToLabwareOrigin(
  parentDefinition: LabwareDefinition,
  childDefinition: LabwareDefinition
): Vector3D {
  // todo(mm, 2025-07-03): When stackingOffsetWithLabware is removed from labware schema
  // 3 and replaced with locating features, use those here.
  const base = {
    x: 0,
    y: 0,
    z: getSchema2Dimensions(parentDefinition).zDimension,
  }
  const adjustment =
    childDefinition.stackingOffsetWithLabware?.[
      parentDefinition.parameters.loadName
    ] ?? IDENTITY_VECTOR
  return getVectorSum(base, {
    x: adjustment.x,
    y: adjustment.y,
    z: -adjustment.z,
  })
}

/**
 * Given a module, computes the offset from the origin (front-left, -x,-y corner) of the
 * deck slot that the module is in, to the origin (again, front-left, -x,-y corner) of
 * the slot atop that module.
 *
 * Some modules, like the Thermocycler and Heater-Shaker, do not physically have slots.
 * In those cases, this returns the corner of what I'll call the module's "labware
 * area", which is roughly a slot-sized area centered over where a labware would go.
 * We're trying to move away from that idea for actual robot path planning, but it's
 * good enough for UI purposes.
 *
 * On a Flex, modules are not really "in deck slots". There, "the deck slot that the
 * module is in" means "the deck slot that the module replaces."
 */
export function getModuleParentOriginToChildSlotOrigin(
  deckId: string | null,
  slotId: string | null,
  moduleDefinition: ModuleDefinition
): Vector3D {
  const transformsForLocation =
    deckId != null && slotId != null
      ? (moduleDefinition.slotTransforms[deckId]?.[slotId] ?? {})
      : {}
  const labwareOffsetTransform =
    transformsForLocation.labwareOffset ?? IDENTITY_AFFINE_TRANSFORM
  const [[x], [y], [z]] = multiplyMatrices(labwareOffsetTransform, [
    [moduleDefinition.labwareOffset.x],
    [moduleDefinition.labwareOffset.y],
    [moduleDefinition.labwareOffset.z],
    [1],
  ])
  return { x, y, z }
}

/**
 * Return a definition's cornerOffsetFromSlot in the style of labware schema 2.
 *
 * @deprecated This is probably an inherently wrong interface to attempt, for a couple
 * reasons.
 *
 * FIRST, depending on what the call site does with this, it might expect the return
 * value to be either:
 *
 * - The vector from the (-x, -y) corner of the parent slot to the (-x, -y) corner of
 *   the labware
 * - The vector from the (-x, -y) corner of the parent slot to the origin of the
 *   labware
 *
 * In labware schema 2, these were the same thing, but not in schema 3.
 *
 * SECOND, in labware schema 2, how a child labware sits on its parent is almost
 * entirely a function of the child's definition. In labware schema 3, it is
 * much more dependent on information only available in the parent. For example, to
 * line up a child's well A1 with a parent's well A1, we need to know where the
 * parent's well A1 is. (Schema 2 just did not attempt to support that).
 *
 * For schema-2 definitions, this will return cornerOffsetFromSlot as-is.
 *
 * For schema-3 definitions, this currently returns the placeholder (12, 34, 56), which
 * is certainly wrong and will cause all manner of visual mayhem, but at least it's
 * wrong in an obvious way and it unblocks importing schema-3 definitions. Solving this
 * properly is call-site-dependent, as described above.
 */
export function getSchema2CornerOffsetFromSlot(
  definition: LabwareDefinition
): LabwareDefinition2['cornerOffsetFromSlot'] {
  if (definition.schemaVersion === 2) {
    return definition.cornerOffsetFromSlot
  } else {
    console.warn(
      `getSchema2CornerOffsetFromSlot() called on schema-3 labware ${definition.parameters.loadName}.` +
        ` Returning bogus data. The labware will render in the wrong place.`
    )
    return {
      x: 12,
      y: 34,
      z: 56,
    }
  }
}
